/**
 * Where a block action that goes nowhere gets recorded.
 *
 * `runBlockAction` returns `'unhandled'` for five of the eight action ids —
 * `request-sales-contact`, `contact-business`, `who-is-admin`,
 * `view-business-plans` and `view-consumer-offers` — because there is no assisted
 * sales handoff, no account-administration page and no consumer eShop in this POC,
 * and no source says what any of them would do (Q-14). Every caller currently
 * ignores that result, which is the right product behaviour: a button that goes
 * nowhere is a visible gap, and a button wired to an invented page is an invented
 * requirement.
 *
 * But "the screen does nothing" and "the screen has a bug" look identical from the
 * outside, and the whole argument for leaving them unwired collapses if a reviewer
 * reads the silence as an oversight. So the result is logged, and the dev overlay
 * names it — an unimplemented action belongs on the panel that exists to say what is
 * unimplemented.
 *
 * ### Why this is module state and not a service
 *
 * `runBlockAction` is a pure function over `Deps`, deliberately: it is called from
 * three places and none of them should have to be an injection context to press a
 * button. Threading a recorder through `Deps` would make every caller carry a
 * dev-only dependency, and making the function injectable would turn a twenty-line
 * mapping into a service to satisfy a log.
 *
 * Signals do not need DI. The panel reads {@link unhandledActions} directly, and
 * because this module is only ever imported by `block-actions.ts` and the overlay,
 * nothing product-facing can reach the writer.
 */
import { computed, signal } from '@angular/core';

export interface UnhandledAction {
  /** The action id `orange-blocked-state` emitted. */
  readonly action: string;
  /** The URL the block was standing on, so the same id on two screens stays apart. */
  readonly url: string;
  /** Wall clock. `Date.now()`, not `MockClock` — this is an interaction, not an order. */
  readonly at: number;
  /** How many times this action+url pair has been pressed. */
  readonly count: number;
}

/**
 * Capped so a demo where someone leans on a dead button does not grow without
 * bound. Fifty is far past the point where the panel stops being readable.
 */
const LIMIT = 50;

const log = signal<readonly UnhandledAction[]>([]);

/** Newest first. Read by the overlay's Diagnostic tab. */
export const unhandledActions = log.asReadonly();

/** Distinct action+url pairs, for the panel's tab badge. */
export const unhandledActionCount = computed(() => log().length);

/**
 * Called by `runBlockAction` on its `'unhandled'` branch, and by nothing else.
 *
 * Repeats collapse into a count rather than stacking, because a customer pressing
 * the same dead button four times is one gap seen four times, not four gaps.
 */
export function recordUnhandledAction(action: string, url: string): void {
  const existing = log().find((entry) => entry.action === action && entry.url === url);

  if (existing) {
    log.update((entries) => [
      { ...existing, at: Date.now(), count: existing.count + 1 },
      ...entries.filter((entry) => entry !== existing),
    ]);
    return;
  }

  log.update((entries) => [{ action, url, at: Date.now(), count: 1 }, ...entries].slice(0, LIMIT));
}

/** The overlay's clear control. Not a reset of anything the app can see. */
export function clearUnhandledActions(): void {
  log.set([]);
}
